/**
 * Shared date utility functions for session date handling
 * Used by both upcoming and recently completed sessions selectors
 */

import { Session } from "../sessions/models/Session";
import {
  getDateAndDisplayTimeLabelInUserTimezone,
  getUserTimezone,
  parseDateWithTimezone,
} from "./timezoneUtils";

/**
 * Interface for grouped sessions by date
 */
export interface GroupedSessions {
  dateLabel: string;
  dateDisplay: string;
  sessions: Session[]; // Generic to work with any session type
}

/**
 * Format a Date object to dd/mm/yyyy string format with optional timezone adjustment.
 * Example: Date for Nov 4, 2025 -> "04/11/2025"
 * With timezone: 22:00 GMT+2 on Nov 3 -> "04/11/2025" (shows next day due to timezone)
 *
 * @param date - The date to format
 * @param timezoneOffsetMinutes - Optional timezone offset in minutes (positive for GMT+, negative for GMT-)
 *                               Example: GMT+2 = 120, GMT-5 = -300
 * @returns Formatted date string in dd/mm/yyyy format
 */
export function formatDateToDDMMYYYY(
  date: Date,
  timezoneOffsetMinutes?: number
): string {
  let adjustedDate = date;

  if (timezoneOffsetMinutes !== undefined) {
    // Create a new date adjusted for the timezone offset
    const adjustedTime = date.getTime() + timezoneOffsetMinutes * 60000;
    adjustedDate = new Date(adjustedTime);
  }

  const day = String(adjustedDate.getDate()).padStart(2, "0");
  const month = String(adjustedDate.getMonth() + 1).padStart(2, "0");
  const year = adjustedDate.getFullYear();
  return `${day}/${month}/${year}`;
}

/**
 * Converts a session dateStart (ISO string) to a UTC date representing the same date/time in the user's timezone.
 * This ensures consistent date grouping regardless of timezone differences.
 *
 * @param dateStart - The session dateStart as an ISO string
 * @returns A UTC Date object representing the user timezone date/time
 */
export function convertDateStartToUtc(dateStart: string): Date {
  const sessionDate = new Date(dateStart);
  const userTimezone = getUserTimezone();

  // Get the date components in user's timezone
  const userTimezoneFormatter = new Intl.DateTimeFormat("en-CA", {
    timeZone: userTimezone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  });

  const parts = userTimezoneFormatter.formatToParts(sessionDate);
  const year = parseInt(parts.find((p) => p.type === "year")?.value || "0");
  const month =
    parseInt(parts.find((p) => p.type === "month")?.value || "0") - 1; // Month is 0-indexed
  const day = parseInt(parts.find((p) => p.type === "day")?.value || "0");
  const hour = parseInt(parts.find((p) => p.type === "hour")?.value || "0");
  const minute = parseInt(parts.find((p) => p.type === "minute")?.value || "0");
  const second = parseInt(parts.find((p) => p.type === "second")?.value || "0");

  // Create a proper UTC date representing the user timezone date/time
  return new Date(Date.UTC(year, month, day, hour, minute, second));
}

/**
 * Format date label with day name and full month name like "Mon, January 15"
 */
const formatDateLabel = (date: Date): string => {
  const days = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
  const months = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];
  date = new Date(date);
  const dayName = days[date.getDay()];
  const monthName = months[date.getMonth()];
  const dayNumber = date.getDate();

  return `${dayName}, ${monthName} ${dayNumber}`;
};

/**
 * Utility function to format date labels for recently completed sessions
 */
export function extractDateLabelAndDisplayDate(
  dateString: string,
  dayFormat: boolean = false
): {
  label: string;
  display: string;
} {
  const date = parseDateWithTimezone(dateString);
  if (!date) {
    return { label: "Invalid", display: "Date" };
  }

  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const sessionDate = new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate()
  );

  // Check if it's today
  if (sessionDate.getTime() === today.getTime()) {
    return {
      label: dayFormat ? formatDateLabel(date) : "Today",
      display: date.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
      }),
    };
  }

  // Check if it's tomorrow
  const tomorrow = new Date(today);
  tomorrow.setDate(tomorrow.getDate() + 1);
  if (sessionDate.getTime() === tomorrow.getTime()) {
    return {
      label: dayFormat ? formatDateLabel(date) : "Tomorrow",
      display: date.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
      }),
    };
  }

  // Check if it's yesterday
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  if (sessionDate.getTime() === yesterday.getTime()) {
    return {
      label: dayFormat ? formatDateLabel(date) : "Yesterday",
      display: date.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
      }),
    };
  }

  // Check if it's within the last week
  const weekAgo = new Date(today);
  weekAgo.setDate(weekAgo.getDate() - 7);

  if (sessionDate >= weekAgo) {
    return {
      label: dayFormat
        ? formatDateLabel(date)
        : date.toLocaleDateString("en-US", { weekday: "short" }),
      display: date.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
      }),
    };
  }

  // For older dates, show the month abbreviation
  return {
    label: dayFormat
      ? formatDateLabel(date)
      : date.toLocaleDateString("en-US", { month: "short" }),
    display: date.toLocaleDateString("en-US", { day: "numeric" }),
  };
}

/**
 * Generic function to group sessions by date with customizable sorting
 */
export function groupSessionsByDate(
  sessions: Session[],
  maxSessions: number = 1,
  sortOrder: "asc" | "desc" = "asc"
): GroupedSessions[] {
  // Limit to max sessions
  const limitedSessions = sessions.slice(0, maxSessions);

  // Group sessions by date
  const groupedMap = new Map<string, GroupedSessions>();

  limitedSessions.forEach((session) => {
    // Convert session.dateStart (ISO string) to user's timezone for proper grouping
    const userTimezoneDate = convertDateStartToUtc(session.dateStart);

    const dateKey = formatDateToDDMMYYYY(userTimezoneDate);
    // Use the timezone-converted date for consistent labeling
    const { label, display } = getDateAndDisplayTimeLabelInUserTimezone(
      userTimezoneDate.toISOString()
    );
    if (!groupedMap.has(dateKey)) {
      groupedMap.set(dateKey, {
        dateLabel: label,
        dateDisplay: display,
        sessions: [],
      });
    }

    groupedMap.get(dateKey)!.sessions.push(session);
  });

  // Convert map to array
  const groupedArray = Array.from(groupedMap.values());

  // Sort sessions within each date group by time using timeLocal property
  groupedArray.forEach((group) => {
    group.sessions.sort((a, b) => {
      // Convert timeLocal (HH:mm format) to minutes for comparison
      const [hoursA, minutesA] = a.timeLocal.split(":").map(Number);
      const [hoursB, minutesB] = b.timeLocal.split(":").map(Number);
      const timeMinutesA = hoursA * 60 + minutesA;
      const timeMinutesB = hoursB * 60 + minutesB;

      return sortOrder === "asc"
        ? timeMinutesA - timeMinutesB
        : timeMinutesB - timeMinutesA;
    });
  });

  // Sort date groups by date using UTC timestamps
  groupedArray.sort((a, b) => {
    // Convert dateStart to UTC for consistent comparison
    const dateA = new Date(a.sessions[0].dateStart).getTime();
    const dateB = new Date(b.sessions[0].dateStart).getTime();
    return sortOrder === "asc" ? dateA - dateB : dateB - dateA;
  });

  return groupedArray;
}
